import { z } from 'zod';

export const uuidSchema = z.string().uuid();

/** Human-authored stable key: URL-safe, lowercase, useful in configuration. */
export const resourceKeySchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'must be lowercase alphanumeric with hyphens');

/** All timestamps cross the wire as RFC 3339 UTC and are formatted client-side (section 16). */
export const timestampSchema = z.string().datetime({ offset: true });

export const localeSchema = z.enum(['en', 'fi', 'sv']);
export type Locale = z.infer<typeof localeSchema>;
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Cursor pagination. Offset pagination is deliberately not offered: it drifts
 * under concurrent writes and degrades on deep pages, and section 10.1
 * requires bounded traversal.
 */
export const paginationQuerySchema = z.object({
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    /** Omitted when counting would require an unbounded scan. */
    totalCount: z.number().int().nonnegative().optional(),
  });
}
export type Page<T> = { items: T[]; nextCursor: string | null; totalCount?: number };

export const sortDirectionSchema = z.enum(['asc', 'desc']);

/**
 * Optimistic concurrency (architecture review gap: section 10.1 covered
 * conditional reads but not write conflicts). Mutating endpoints accept the
 * version last read and return 412 with `resource.version_mismatch` on drift.
 */
export const versionedSchema = z.object({ version: z.number().int().nonnegative() });

export const idempotencyKeySchema = z.string().min(8).max(255);

/**
 * Severity as a colour, semantically rather than literally.
 *
 * The API says a thing is `danger`; the theme decides what danger looks like in
 * this deployment's palette and colour mode. A hex value crossing the wire
 * would be a brand colour the backend had opinions about, which section 17
 * rules out.
 *
 * Colour is never the only carrier. Anything drawn in a tone also states its
 * status in words, because a red dot says nothing to a screen reader and little
 * to a reader who cannot separate it from the green one (section 18).
 *
 * Shared rather than per-module: the map and the cross-reference list show the
 * same service in the same colour, and two enums with the same five members
 * would eventually stop having the same five members.
 */
export const STATUS_TONES = ['neutral', 'info', 'success', 'warning', 'danger'] as const;
export const statusToneSchema = z.enum(STATUS_TONES);
export type StatusTone = (typeof STATUS_TONES)[number];
